import React, { useEffect, useRef, useMemo } from "react";
import { Box } from "@mui/material";
import { calculateNiceScale, generateYLabelsWithPositions } from "../../PagesContent/BmsFaults/Analytics/ScrollableColumnChart/ChartScaleUtils";
import ScrollableChartWrapper from "../../PagesContent/BmsFaults/Analytics/ScrollableColumnChart/ScrollableChartWrapper";
import RenderChartLegend from "../../PagesContent/AdvancedAnalytics/ContinuousChart/RenderChartLegend";

const CHART_ID = "severity-of-alerts-chart";
const SEVERITY_SERIES_NAMES = ["Warning", "High Severity", "Critical"];
const CHART_HEIGHT = 315;
const TOP_OFFSET = 45;
const BOTTOM_OFFSET = 37;

const SeverityOfAlertsApexChart = ({
  chartData = { categories: [], warningData: [], highSeverityData: [], criticalData: [], vehiclesAffectedData: [] },
  isSidebarExpanded = false,
  onBarClick,
  baseEntityTypePluralLable = "Batteries",
}) => {
  const onBarClickRef = useRef(onBarClick);
  useEffect(() => { onBarClickRef.current = onBarClick; }, [onBarClick]);

  const chartDataRef = useRef(chartData);
  useEffect(() => { chartDataRef.current = chartData; }, [chartData]);

  const { series, chartOptions, legendSeries, faultLabels, vehiclesLabels, minWidth } = useMemo(() => {
    const {
      warningData = [],
      highSeverityData = [],
      criticalData = [],
      vehiclesAffectedData = [],
      categories = [],
    } = chartData;

    const maxBarValue    = Math.max(...warningData, ...highSeverityData, ...criticalData, 0);
    const MIN_HEIGHT     = maxBarValue > 0 ? maxBarValue * 0.08 : 1;
    const toVisual       = (val) => (val === 0 ? 0 : Math.max(val, MIN_HEIGHT));

    const visualWarning      = warningData.map(toVisual);
    const visualHighSeverity = highSeverityData.map(toVisual);
    const visualCritical     = criticalData.map(toVisual);

    const visualMax     = Math.max(...visualWarning, ...visualHighSeverity, ...visualCritical, 0);
    const faultScale    = calculateNiceScale(0, visualMax, 6);
    const vehiclesScale = calculateNiceScale(0, Math.max(...vehiclesAffectedData, 0), 6);
    const faultLbls     = generateYLabelsWithPositions(faultScale, CHART_HEIGHT, TOP_OFFSET, BOTTOM_OFFSET);
    const vehicleLbls   = generateYLabelsWithPositions(vehiclesScale, CHART_HEIGHT, TOP_OFFSET, BOTTOM_OFFSET);

    const maxVisible = Math.max(Math.floor(800 / 200), 3);
    const mw         = categories.length * 80;

    const s = [
      { name: "Warning",       type: "column", data: visualWarning },
      { name: "High Severity", type: "column", data: visualHighSeverity },
      { name: "Critical",      type: "column", data: visualCritical },
      { name: `${baseEntityTypePluralLable} Affected`, type: "line", data: vehiclesAffectedData },
    ];

    const opts = {
      chart: {
        id: CHART_ID,
        type: "bar",
        height: CHART_HEIGHT,
        toolbar: { show: false },
        animations: { enabled: true, dynamicAnimation: { enabled: true, speed: 300 } },
        events: {
          dataPointSelection: (event, chartContext, config) => {
            if (config.seriesIndex < 3 && onBarClickRef.current) {
              const label             = chartDataRef.current.categories[config.dataPointIndex] || "";
              const originalDateRange = chartDataRef.current.dateRanges?.[config.dataPointIndex] || "";
              const severityName      = SEVERITY_SERIES_NAMES[config.seriesIndex] || "";
              onBarClickRef.current({ label, originalDateRange, seriesIndex: config.seriesIndex, dataPointIndex: config.dataPointIndex, severityName });
            }
          },
        },
      },
      stroke:      { width: [0, 0, 0, 2], curve: "straight" },
      markers:     { size: [0, 0, 0, 3], strokeWidth: 0 },
      colors:      ["#FFCA38", "#FF904B", "#B50500", "#4D44D8"],
      plotOptions: { bar: { columnWidth: "24px", borderRadius: 2 } },
      states: {
        hover:  { filter: { type: "lighten", value: 0.1 } },
        active: { allowMultipleDataPointsSelection: false, filter: { type: "none" } },
      },
      xaxis: {
        categories,
        labels:     { style: { colors: "#494949", fontSize: "12px" } },
        axisBorder: { color: "#E0E0E0" },
        axisTicks:  { color: "#E0E0E0" },
        ...(categories.length > maxVisible && { range: maxVisible }),
      },
      yaxis: [
        { seriesName: "Warning", min: faultScale.min, max: faultScale.max, tickAmount: 5, forceNiceScale: false, labels: { formatter: (v) => v.toFixed(0) }, show: false },
        { seriesName: "Warning", min: faultScale.min, max: faultScale.max, tickAmount: 5, forceNiceScale: false, labels: { formatter: (v) => v.toFixed(0) }, show: false },
        { seriesName: "Warning", min: faultScale.min, max: faultScale.max, tickAmount: 5, forceNiceScale: false, labels: { formatter: (v) => v.toFixed(0) }, show: false },
        { seriesName: `${baseEntityTypePluralLable} Affected`, min: vehiclesScale.min, max: vehiclesScale.max, tickAmount: 5, forceNiceScale: false, labels: { formatter: (v) => v.toFixed(0) }, opposite: true, show: false },
      ],
      legend:     { show: false },
      grid: {
        show: true,
        borderColor: "#F0F0F0",
        strokeDashArray: 3,
        padding: { left: 0, right: 0, top: 15, bottom: 0 },
        xaxis: { lines: { show: false } },
      },
      dataLabels: { enabled: false },
      tooltip: {
        shared: true,
        intersect: false,
        followCursor: true,
        style: { fontSize: "12px" },
        y: {
          formatter: (val, opts2) => {
            const i  = opts2.dataPointIndex;
            const sv = opts2.seriesIndex;
            const d  = chartDataRef.current;
            if (sv === 0) return (d.warningData?.[i]          || 0) + " alerts";
            if (sv === 1) return (d.highSeverityData?.[i]     || 0) + " alerts";
            if (sv === 2) return (d.criticalData?.[i]         || 0) + " alerts";
            return (d.vehiclesAffectedData?.[i] || 0) + ` ${baseEntityTypePluralLable.toLowerCase()}`;
          },
        },
      },
    };

    const legend = [
      { name: "Warning",       color: "#FFCA38" },
      { name: "High Severity", color: "#FF904B" },
      { name: "Critical",      color: "#B50500" },
      { name: `${baseEntityTypePluralLable} Affected`, color: "#4D44D8" },
    ];

    return { series: s, chartOptions: opts, legendSeries: legend, faultLabels: faultLbls, vehiclesLabels: vehicleLbls, minWidth: mw };
  }, [chartData, baseEntityTypePluralLable]);

  return (
    <Box sx={{
      mt: 3, width: "100%", overflow: "hidden",
      "& .apexcharts-bar-area": { cursor: "pointer" },
      "& .apexcharts-marker": { cursor: "pointer" },
    }}>
      <Box sx={{ mb: 2 }}>
        <RenderChartLegend
          legendSeries={legendSeries}
          chartIDs={[CHART_ID]}
          isLoading={false}
          centered={true}
        />
      </Box>
      <ScrollableChartWrapper
        chartOptions={chartOptions}
        series={series}
        chartType="bar"
        chartHeight={CHART_HEIGHT}
        leftAxis={{ label: "Alert Count", labelData: faultLabels }}
        rightAxis={{ label: `Number of ${baseEntityTypePluralLable} Affected`, labelData: vehiclesLabels }}
        isSidebarExpanded={isSidebarExpanded}
        minWidth={minWidth}
        categoriesCount={chartData.categories?.length || 0}
        drillDownEnabled={false}
      />
    </Box>
  );
};

export default SeverityOfAlertsApexChart;
